import type { IPoint } from "@fluentui/react/lib/Utilities";
import type { SelectionDirection, TableView } from "owa-mail-list-store";
import MailListItemSelectionSource from "owa-mail-store/lib/store/schema/MailListItemSelectionSource";
import { action } from "satcheljs";

/**
 * Toggle selection state on the given row
 * @param tableView where the selection took place
 * @param rowKey the rowKey of the item to toggle selection on
 * @param isUserNavigation whether user's click or action triggered this action
 * @param mailListItemSelectionSource The source of selection on mail item
 */
export interface TOGGLE_SELECT_ROWActionMessage {
    tableView: TableView;
    rowKey: string;
    isUserNavigation: boolean;
    mailListItemSelectionSource: MailListItemSelectionSource;
}
export const toggleSelectRow: (tableView: TableView, rowKey: string, isUserNavigation: boolean, mailListItemSelectionSource: MailListItemSelectionSource) => TOGGLE_SELECT_ROWActionMessage = action("TOGGLE_SELECT_ROW", (tableView: TableView, rowKey: string, isUserNavigation: boolean, mailListItemSelectionSource: MailListItemSelectionSource): TOGGLE_SELECT_ROWActionMessage => ({
    tableView,
    rowKey,
    isUserNavigation,
    mailListItemSelectionSource,
}));

/**
 * After promise has returned following a selection change action
 * @param rowKey the rowKey of the item that selection changed on
 * @param tableView where the selection took place
 * @param mailListItemSelectionSource The source of selection on mail item
 */
export interface ON_AFTER_SELECTION_CHANGEDActionMessage {
    rowKey: string;
    tableView: TableView;
    mailListItemSelectionSource: MailListItemSelectionSource;
}
export const onAfterSelectionChanged: (rowKey: string, tableView: TableView, mailListItemSelectionSource: MailListItemSelectionSource) => ON_AFTER_SELECTION_CHANGEDActionMessage = action("ON_AFTER_SELECTION_CHANGED", (rowKey: string, tableView: TableView, mailListItemSelectionSource: MailListItemSelectionSource): ON_AFTER_SELECTION_CHANGEDActionMessage => ({
    rowKey,
    tableView,
    mailListItemSelectionSource,
}));

/**
 * Reset the list view expansion state (expanded conversation)
 */
export const resetListViewExpansionViewState: () => {} = action("RESET_LIST_VIEW_EXPANSION_VIEW_STATE");

/**
 * Set the list view expansion state to second level expansion by setting loading to false and latestItemIdsInEachFork to null
 */
export interface SET_SECOND_LEVEL_LIST_VIEW_EXPANSIONActionMessage {
    rowKey: string;
}
export const setSecondLevelListViewExpansion: (rowKey: string) => SET_SECOND_LEVEL_LIST_VIEW_EXPANSIONActionMessage = action("SET_SECOND_LEVEL_LIST_VIEW_EXPANSION", (rowKey: string): SET_SECOND_LEVEL_LIST_VIEW_EXPANSIONActionMessage => ({ rowKey }));

/**
 * Called when an item part is selected
 * @param nodeId the nodeId of the selected item part
 * @param itemId the itemId of the selected item part
 * @param allNodeIds the nodeIds of the conversation
 * @param tableView - where the selection took place
 * @param mailListItemSelectionSource The source of selection on item part
 */
export interface ITEM_PART_SELECTEDActionMessage {
    nodeId: string;
    itemId: string;
    allNodeIds: string[];
    tableView: TableView;
    mailListItemSelectionSource: MailListItemSelectionSource;
}
export const itemPartSelected: (nodeId: string, itemId: string, allNodeIds: string[], tableView: TableView, mailListItemSelectionSource: MailListItemSelectionSource) => ITEM_PART_SELECTEDActionMessage = action("ITEM_PART_SELECTED", (nodeId: string, itemId: string, allNodeIds: string[], tableView: TableView, mailListItemSelectionSource: MailListItemSelectionSource): ITEM_PART_SELECTEDActionMessage => ({
    nodeId,
    itemId,
    allNodeIds,
    tableView,
    mailListItemSelectionSource,
}));

/**
 * Called when an item part is selected
 * @param rowKey the rowKey for the parent conversation of the item part
 * @param tableView - where the delete took place
 */
export interface SELECT_NEXT_ITEM_PARTActionMessage {
    rowKey: string;
    tableView: TableView;
}
export const selectNextItemPart: (rowKey: string, tableView: TableView) => SELECT_NEXT_ITEM_PARTActionMessage = action("SELECT_NEXT_ITEM_PART", (rowKey: string, tableView: TableView): SELECT_NEXT_ITEM_PARTActionMessage => ({
    rowKey,
    tableView,
}));

/**
 * Called to open the context menu for an expanded item part
 * @param contextMenuAnchor anchor from which the context menu will open
 * @param nodeId the Id of the item part that was selected
 * @param tableViewId the id of the table where the conversation belongs
 */
export interface ON_ITEM_PART_CONTEXT_MENUActionMessage {
    contextMenuAnchor: IPoint;
    nodeId: string;
    tableViewId: string;
}
export const onItemPartContextMenu: (contextMenuAnchor: IPoint, nodeId: string, tableViewId: string) => ON_ITEM_PART_CONTEXT_MENUActionMessage = action("ON_ITEM_PART_CONTEXT_MENU", (contextMenuAnchor: IPoint, nodeId: string, tableViewId: string): ON_ITEM_PART_CONTEXT_MENUActionMessage => ({
    contextMenuAnchor,
    nodeId,
    tableViewId,
}));

/**
 * Select a single expanded item part
 * @param rowKey the instance key of the conversation
 * @param nodeIdToSelect the nodeId of the item part to select
 * @param allNodeIds of the conversation
 * @param tableViewId the id of the table where the conversation belongs
 * @param currentSelectedNodeIds the ids of currently selected itemparts in expanded conversation
 */
export interface SINGLE_SELECT_ITEM_PARTActionMessage {
    rowKey: string;
    nodeIdToSelect: string;
    allNodeIds: string[];
    tableViewId: string;
    currentSelectedNodeIds: string[] | undefined;
}
export const singleSelectItemPart: (rowKey: string, nodeIdToSelect: string, allNodeIds: string[], tableViewId: string, currentSelectedNodeIds?: string[]) => SINGLE_SELECT_ITEM_PARTActionMessage = action("SINGLE_SELECT_ITEM_PART", (rowKey: string, nodeIdToSelect: string, allNodeIds: string[], tableViewId: string, currentSelectedNodeIds?: string[]): SINGLE_SELECT_ITEM_PARTActionMessage => ({
    rowKey,
    nodeIdToSelect,
    allNodeIds,
    tableViewId,
    currentSelectedNodeIds,
}));

/**
 * Toggle selection on an item part
 * @param nodeId the nodeId of the item part to toggle selection
 * @param isCtrlOrCmdKeyDown - whether the Control or Command key is down for this operation
 */
export interface TOGGLE_SELECT_ITEM_PARTActionMessage {
    nodeId: string;
    isCtrlOrCmdKeyDown: boolean;
}
export const toggleSelectItemPart: (nodeId: string, isCtrlOrCmdKeyDown: boolean) => TOGGLE_SELECT_ITEM_PARTActionMessage = action("TOGGLE_SELECT_ITEM_PART", (nodeId: string, isCtrlOrCmdKeyDown: boolean): TOGGLE_SELECT_ITEM_PARTActionMessage => ({
    nodeId,
    isCtrlOrCmdKeyDown,
}));

export interface ON_ITEM_PART_SELECTED_OR_CHECKEDActionMessage {
    nodeId: string;
    tableView: TableView;
    isCtrlOrCmdKeyDown: boolean;
}
export const onItemPartSelectedOrChecked: (nodeId: string, tableView: TableView, isCtrlOrCmdKeyDown: boolean) => ON_ITEM_PART_SELECTED_OR_CHECKEDActionMessage = action("ON_ITEM_PART_SELECTED_OR_CHECKED", (nodeId: string, tableView: TableView, isCtrlOrCmdKeyDown: boolean): ON_ITEM_PART_SELECTED_OR_CHECKEDActionMessage => ({
    nodeId,
    tableView,
    isCtrlOrCmdKeyDown,
}));

/**
 * Perform range selection from current selection to target item part's node id
 * @param tableView where the selection took place
 * @param targetNodeId the nodeId of the item to extend range selection to
 * @param isCtrlOrCmdKeyDown - whether the Control or Command key is down for this operation
 */
export interface RANGE_SELECT_ITEM_PARTActionMessage {
    tableView: TableView;
    targetNodeId: string;
    isCtrlOrCmdKeyDown: boolean;
}
export const rangeSelectItemPart: (tableView: TableView, targetNodeId: string, isCtrlOrCmdKeyDown: boolean) => RANGE_SELECT_ITEM_PARTActionMessage = action("RANGE_SELECT_ITEM_PART", (tableView: TableView, targetNodeId: string, isCtrlOrCmdKeyDown: boolean): RANGE_SELECT_ITEM_PARTActionMessage => ({
    tableView,
    targetNodeId,
    isCtrlOrCmdKeyDown,
}));

/**
 * Called when an item part should become the anchor for multi-selection purposes
 * @param nodeId the nodeId of the item part that should be set as the anchor
 */
export interface SET_ANCHOR_NODE_IN_EXPANSIONActionMessage {
    nodeId: string;
}
export const setAnchorNodeInExpansion: (nodeId: string) => SET_ANCHOR_NODE_IN_EXPANSIONActionMessage = action("SET_ANCHOR_NODE_IN_EXPANSION", (nodeId: string): SET_ANCHOR_NODE_IN_EXPANSIONActionMessage => ({
    nodeId,
}));

/**
 * Toggle between select all and clear selection on the table
 * @param tableView where the operation is being performed
 */
export interface TOGGLE_SELECT_ALL_ROWSActionMessage {
    tableView: TableView;
}
export const toggleSelectAllRows: (tableView: TableView) => TOGGLE_SELECT_ALL_ROWSActionMessage = action("TOGGLE_SELECT_ALL_ROWS", (tableView: TableView): TOGGLE_SELECT_ALL_ROWSActionMessage => ({
    tableView,
}));

/**
 * Perform range selection from current selection to target row key
 * @param tableView where the selection took place
 * @param targetRowKey the rowKey of the item to extend range selection to
 * @param mailListItemSelectionSource The source of selection on mail item
 * @param isCtrlOrCmdKeyDown - whether the Control or Command key is down for this operation
 */
export interface RANGE_SELECT_ROWActionMessage {
    tableView: TableView;
    targetRowKey: string;
    mailListItemSelectionSource: MailListItemSelectionSource;
    isCtrlOrCmdKeyDown: boolean | undefined;
}
export const rangeSelectRow: (tableView: TableView, targetRowKey: string, mailListItemSelectionSource: MailListItemSelectionSource, isCtrlOrCmdKeyDown?: boolean) => RANGE_SELECT_ROWActionMessage = action("RANGE_SELECT_ROW", (tableView: TableView, targetRowKey: string, mailListItemSelectionSource: MailListItemSelectionSource, isCtrlOrCmdKeyDown?: boolean): RANGE_SELECT_ROWActionMessage => ({
    tableView,
    targetRowKey,
    mailListItemSelectionSource,
    isCtrlOrCmdKeyDown,
}));

/**
 * Resets selection in the given table
 * @param tableView where the operation is being performed
 * @param mailListItemSelectionSource selection mode for the maillist item
 */
export interface RESET_SELECTIONActionMessage {
    tableView: TableView;
    mailListItemSelectionSource: MailListItemSelectionSource;
    isUserNavigation?: boolean;
}
export const resetSelection: (tableView: TableView, mailListItemSelectionSource: MailListItemSelectionSource, isUserNavigation?: boolean) => RESET_SELECTIONActionMessage = action("RESET_SELECTION", (tableView: TableView, mailListItemSelectionSource: MailListItemSelectionSource, isUserNavigation?: boolean): RESET_SELECTIONActionMessage => ({
    tableView,
    mailListItemSelectionSource,
    isUserNavigation,
}));

/**
 * Resets selection in the given table
 * @param tableView where the operation is being performed
 */
export interface RESET_SELECTIONActionMessage {
    tableView: TableView;
}
export const resetSelectionOnTable: (tableView: TableView) => RESET_SELECTIONActionMessage = action("RESET_SELECTION", (tableView: TableView): RESET_SELECTIONActionMessage => ({
    tableView,
    mailListItemSelectionSource: MailListItemSelectionSource.Auto,
    isUserNavigation: undefined,
}));

/**
 * Select the next row in the direction given
 * @param tableView where the operation is being performed
 * @param selectionDirection the direction in which to select next row
 * @param mailListItemSelectionSource selection mode for the maillist item
 */
export interface SELECT_ROW_IN_DIRECTIONActionMessage {
    tableView: TableView;
    selectionDirection: SelectionDirection;
    mailListItemSelectionSource: MailListItemSelectionSource;
}
export const selectRowInDirection: (tableView: TableView, selectionDirection: SelectionDirection, mailListItemSelectionSource: MailListItemSelectionSource) => SELECT_ROW_IN_DIRECTIONActionMessage = action("SELECT_ROW_IN_DIRECTION", (tableView: TableView, selectionDirection: SelectionDirection, mailListItemSelectionSource: MailListItemSelectionSource): SELECT_ROW_IN_DIRECTIONActionMessage => ({
    tableView,
    selectionDirection,
    mailListItemSelectionSource,
}));

export interface SET_EXPANSION_FOR_ROWActionMessage {
    rowKey: string;
    mailListItemSelectionSource: MailListItemSelectionSource;
}
export const setExpansionForRow: (rowKey: string, mailListItemSelectionSource: MailListItemSelectionSource) => SET_EXPANSION_FOR_ROWActionMessage = action("SET_EXPANSION_FOR_ROW", (rowKey: string, mailListItemSelectionSource: MailListItemSelectionSource): SET_EXPANSION_FOR_ROWActionMessage => ({
    rowKey,
    mailListItemSelectionSource,
}));

/**
 * Select the row in the direction given in multiselect mode
 * @param tableView where the operation is being performed
 * @param mailListItemSelectionSource selection mode for the maillist item
 * @param selectionDirection direction in which the keyboard multiselection is happening (shift-up == Next, shift-down == Previous)
 * @param isCtrlOrCmdKeyDown whether user is also holding down ctrl or command key
 */
export interface KEYBOARD_MULTI_SELECT_ROWActionMessage {
    tableView: TableView;
    mailListItemSelectionSource: MailListItemSelectionSource;
    selectionDirection: SelectionDirection;
    isCtrlOrCmdKeyDown: boolean;
}
export const keyboardMultiSelectRow: (tableView: TableView, mailListItemSelectionSource: MailListItemSelectionSource, selectionDirection: SelectionDirection, isCtrlOrCmdKeyDown: boolean) => KEYBOARD_MULTI_SELECT_ROWActionMessage = action("KEYBOARD_MULTI_SELECT_ROW", (tableView: TableView, mailListItemSelectionSource: MailListItemSelectionSource, selectionDirection: SelectionDirection, isCtrlOrCmdKeyDown: boolean): KEYBOARD_MULTI_SELECT_ROWActionMessage => ({
    tableView,
    mailListItemSelectionSource,
    selectionDirection,
    isCtrlOrCmdKeyDown,
}));

/**
 * Collapse a given conversation when a user gives a keydown command
 * @param rowKey the row to collapse
 */
export interface KEYBOARD_COLLAPSE_CONVERSATIONActionMessage {
    rowKey: string;
    tableView: TableView | undefined;
}
export const keyboardCollapseConversation: (rowKey: string, tableView: TableView | undefined) => KEYBOARD_COLLAPSE_CONVERSATIONActionMessage = action("KEYBOARD_COLLAPSE_CONVERSATION", (rowKey: string, tableView: TableView | undefined): KEYBOARD_COLLAPSE_CONVERSATIONActionMessage => ({
    rowKey,
    tableView,
}));

/**
 * Selects a new row on row removal
 * @param tableView where the operation is being performed
 * @param lastSelectedRowIndex index of the row that was last selected
 * @param lastSelectedRowWasPinned whether last selected row was pinned
 * @param isRowNotRemovedYet whether the row has not been removed yet from the tableView
 * @param rowKeysBeingRemoved all rows in the pending removal batch
 */
export interface SELECT_NEW_ITEM_UPON_ROW_REMOVALActionMessage {
    tableView: TableView;
    lastSelectedRowIndex: number;
    lastSelectedRowWasPinned: boolean;
    isRowNotRemovedYet: boolean | undefined;
    rowKeysBeingRemoved?: string[];
}
export const selectNewItemUponRowRemoval: (tableView: TableView, lastSelectedRowIndex: number, lastSelectedRowWasPinned: boolean, isRowNotRemovedYet?: boolean, rowKeysBeingRemoved?: string[]) => SELECT_NEW_ITEM_UPON_ROW_REMOVALActionMessage = action("SELECT_NEW_ITEM_UPON_ROW_REMOVAL", (tableView: TableView, lastSelectedRowIndex: number, lastSelectedRowWasPinned: boolean, isRowNotRemovedYet?: boolean, rowKeysBeingRemoved?: string[]): SELECT_NEW_ITEM_UPON_ROW_REMOVALActionMessage => ({
    tableView,
    lastSelectedRowIndex,
    lastSelectedRowWasPinned,
    isRowNotRemovedYet,
    rowKeysBeingRemoved,
}));
